import { ListaWithdrawalAbi } from '../abis/lista'
import type { LstWithdrawalRequest } from '../types'
import { toBigInt, type LstWithdrawalReader } from './shared'

/**
 * Lista slisBNB — per-user withdrawal-request enumeration on the
 * ListaStakeManager.
 *
 * Stage 1: `getUserWithdrawalRequests(user)` → open requests
 *          (`uuid`, `amountInSnBnb`, `startTime`).
 * Stage 2: per index, `getUserRequestStatus(user, idx)` →
 *          `(isClaimable, amount)` where `amount` is the BNB owed.
 *
 * `getUserRequestStatus` is indexed by position in the array returned
 * by stage 1 (not by `uuid`). The StakeManager exposes no per-request
 * ready timestamp — claimability follows the BNB validator unbond
 * (~7–15 days) — so `status` is derived from `isClaimable` and
 * `readyAt` is left unset (mirrors the beHYPE / Kelp readers).
 */
export const readerLista: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const manager = entry.withdrawalContract
    if (!manager) return []

    let stage1: unknown[]
    try {
      stage1 = (await multicallRetry({
        chain: chainId,
        calls: [
          {
            address: manager,
            name: 'getUserWithdrawalRequests',
            params: [user],
          },
        ] as unknown as never[],
        abi: [ListaWithdrawalAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }
    const requests = stage1[0] as unknown[] | undefined
    if (!Array.isArray(requests) || requests.length === 0) return []

    const calls = requests.map((_, i) => ({
      address: manager,
      name: 'getUserRequestStatus',
      params: [user, BigInt(i)],
    }))
    let stage2: unknown[]
    try {
      stage2 = (await multicallRetry({
        chain: chainId,
        calls: calls as unknown as never[],
        abi: calls.map(() => ListaWithdrawalAbi) as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < requests.length; i++) {
      const req = requests[i] as
        | { uuid?: unknown; amountInSnBnb?: unknown }
        | unknown[]
        | undefined
      const status = stage2[i] as
        | { _isClaimable?: unknown; _amount?: unknown }
        | unknown[]
        | undefined
      if (!status) continue

      const isClaimable = Array.isArray(status)
        ? Boolean(status[0])
        : Boolean(status._isClaimable)
      // BNB owed comes from the status getter; fall back to the
      // recorded slisBNB amount if the status read was malformed.
      const bnbAmount = Array.isArray(status)
        ? toBigInt(status[1])
        : toBigInt(status._amount)
      const snbnbAmount = Array.isArray(req)
        ? toBigInt(req[1])
        : toBigInt(req?.amountInSnBnb)
      const amount = bnbAmount ?? snbnbAmount
      if (amount === undefined) continue

      const uuid = Array.isArray(req) ? toBigInt(req[0]) : toBigInt(req?.uuid)
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(uuid ?? i),
        amountUnderlying: amount.toString(),
        status: isClaimable ? 'claimable' : 'pending',
      })
    }
    return out
  },
}
